import { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { Link, useLocation, useParams } from "react-router-dom";
import { useFetchOperation } from '../../service/Operation';
import { Card, Dictionary } from '../../types/Common';
import { LocalDateTime } from '../formatters/DateTimeFormat';
import AutoPaginationTable from '../general/AutoPaginationTable';
import Error from '../general/Error';
import Loader from '../general/Loader';
import { getWiktionaryUrl } from './../card/ExternalLinksDropdown';

interface Mark extends Card {
    dictionaryId: number,
    dictionaryName: string,
    userId: number,
    userName: string,
    dateTime: string,
    phraseWiktionaryLanguageName: string | undefined,
    // computed:
    wiktionaryUrl: string | undefined,
}

export default function Marks() {
    const params = useParams();
    const dictionary = useLocation().state as Dictionary;

    const [marks, setMarks] = React.useState<Mark[]>();

    const [getting, startGetting] = useFetchOperation(onGetSuccess, undefined, true);

    React.useEffect(() => {
        startGetting('get', `api/marks/${params.id ?? ''}`)
        const dictionaryName = dictionary ? ` - ${dictionary.name}` : '';
        document.title = `Marked cards${dictionaryName} - ${(document as any).rootTitle}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id])

    function onGetSuccess(data: Mark[]) {

        data.forEach(x => {
            if (x.phraseWiktionaryLanguageName)
                x.wiktionaryUrl = getWiktionaryUrl(x.phrase, x.phraseWiktionaryLanguageName)
        });
        setMarks(data);
    }

    return (
        <>
            {getting.active ?
                <Loader />
                :
                getting.error ?
                    <Error text={getting.error} />
                    :
                    <>
                        <h1 className='mb-3'>
                            Marked cards
                            {dictionary &&
                                <> of <Link to={`/dictionaries/${dictionary.id}`} className="fw-bold">
                                    {dictionary.name}
                                </Link>
                                </>
                            }
                        </h1>
                        {!marks ?
                            <p className="my-3">No marked cards found</p>
                            :
                            <div className="w-100">
                                <div className="mb-2">{marks.length} marked cards</div>

                                <AutoPaginationTable className="mb-3"
                                    columns={columns}
                                    data={marks} />
                            </div>
                        }
                    </>
            }
        </>
    )
}

const columns: ColumnDef<Mark>[] = [
    {
        header: '#',
        accessorFn: x => x.id,
        cell: props => <Link to={`/cards/${props.row.original.id}`}>{props.row.index + 1}</Link>
    },
    {
        header: 'Phrase',
        accessorFn: x => x.phrase,
        cell: props => props.row.original.wiktionaryUrl ? <a target="_blank" href={props.row.original.wiktionaryUrl}>{props.row.original.phrase}</a> : <>{props.row.original.phrase}</>
    },
    {
        header: 'Meaning',
        accessorFn: x => x.meaning,
    },
    {
        header: 'Pronunciation',
        accessorFn: x => x.pronunciation,
    },
    {
        header: 'Forms',
        accessorFn: x => x.forms,
    },
    {
        header: 'Example',
        accessorFn: x => x.example,
    },
    {
        header: 'Dictionary',
        accessorFn: x => x.dictionaryName,
        cell: props => <Link to={`/dictionaries/${props.row.original.dictionaryId}`}>{props.row.original.dictionaryName}</Link>
    },
    {
        header: 'User',
        accessorFn: x => x.userName,
        cell: props => <Link to={`/user/${props.row.original.userId}`}>{props.row.original.userName}</Link>
    },
    {
        header: 'Date',
        accessorFn: x => x.dateTime,
        cell: props => <LocalDateTime value={props.row.original.dateTime} />
    },
];
